import { ReportSettings } from './ReportSettings';
import { Report, ReportType } from 'types';
import { TranslationFunction } from 'i18next';

//
//  Ensures that the report settings XML is valid when the report type changes.
//
export const setReportType = (report: Readonly<Report>, newReportType: ReportType): Readonly<Report> => {
    // We assume the settings are already valid if the current report type is the same as the target type
    if (report.reportType === newReportType) {
        return report;
    }

    const originalSettings = ReportSettings.fromXml(report.settings);
    let settings = originalSettings;

    const {
        noDevices,
        noOutputGraph,
        hasExitDevices,
        singleCamera,
    } = reportSpecs[newReportType];

    // When switching to a report type that only supports one camera, make sure there is at most one camera selected
    if (singleCamera) {
        const { devices } = settings;
        if (devices.length > 1) {
            // Arbitrarily choose the first camera
            settings = settings.setDevices([ devices[0] ]);
        }
    }

    if (noOutputGraph && settings.output === 'Graph') {
        settings = settings.setOutput('Table');
    }

    if (!hasExitDevices && settings.exitDevices.length > 0) {
        settings = settings.setExitDevices([]);
    }

    if (noDevices && settings.devices.length > 0) {
        settings = settings.setDevices([]);
    }

    const columns =
        reportSpecs[newReportType].columnNames
            .map((name, i) => ({
                name,
                visibleIndex: i,
            }));

    settings = settings.setTableColumns(columns);

    // Choose the first number column as the default for the graph
    const numberColumns = getNumberColumns(newReportType);
    if (numberColumns.length) {
        settings = settings.setGraphDefaultData(numberColumns[0]);
    }

    if (!hasFeature(newReportType, 'hasHeatMap')) {
        settings =
            settings
                .removeEmailFileType('Image')
                .removeSaveFileType('Image')
                .removeHeatMap();
    }

    if (!hasFeature(newReportType, 'hasSummary')) {
        settings = settings.removeEmailFileType('Summary').removeSaveFileType('Summary');
    }

    return {
        ...report,
        reportType: newReportType,
        settings: settings === originalSettings ? report.settings : settings.toXml(),
    };
};

type SortType = 'ID' | 'ALPHA' | 'DATE' | 'TIME' | 'NUMBER' | null;

type ReportSpecs = ReportSpecsFeatures & {
    textKey: string;
    columnNames: ReadonlyArray<string>;
    columnSorts: ReadonlyArray<SortType>;
};

// Defaults are always false
type ReportSpecsFeatures = {
    hasLine?: boolean;
    hasFilter?: boolean;
    hasHeatMap?: boolean;
    hasSummary?: boolean;
    hasExitDevices?: boolean;
    hasSensitivity?: boolean;
    noDevices?: boolean;
    noOutputGraph?: boolean;
    noStartEnd?: boolean;
    noTimeRange?: boolean; // hides both of noTimeStartEnd and noTimeInterval
    noTimeStartEnd?: boolean;
    noTimeInterval?: boolean;
    singleCamera?: boolean;
    includeIntegrations?: boolean;
    requiresAiObjectTracking?: boolean;
    hasUsernames?: boolean;
};

const reportSpecs: { [id: string]: ReportSpecs } = {
    AcrossLine: {
        textKey: '6.1241.0.2',
        columnNames: [ 'Date', 'CameraNm', 'Interval', 'CountIn', 'CountOut', 'PeopleIn', 'PeopleOut', 'CarsIn', 'CarsOut', 'UnknownIn', 'UnknownOut' ],
        columnSorts: [ 'DATE', 'ALPHA', 'TIME', 'NUMBER', 'NUMBER', 'NUMBER', 'NUMBER', 'NUMBER', 'NUMBER', 'NUMBER', 'NUMBER' ],
        hasLine: true,
        hasSummary: true,
    },

    Alarms: {
        textKey: 'Reports_AlarmReport_Name',
        columnNames: ['Date', 'Time', 'CameraNm', 'TriggerNm', 'Rule', 'Algorithms', 'TimeAck', 'AcknowledgeTime', 'ResetTime', 'TimeOfReset', 'User', 'Alarm', 'Comment', 'Classification', 'Thumbnail', 'RelDistance', 'AbsDistance', 'Location', 'Node', 'BulkProcessed' ],
        columnSorts: ['DATE', 'TIME', 'ALPHA', 'ALPHA', 'ALPHA', 'ALPHA', 'TIME', 'NUMBER', 'NUMBER', 'TIME', 'ALPHA', 'ALPHA', 'ALPHA', 'ALPHA', null, 'ALPHA', 'ALPHA', 'ALPHA', 'ALPHA', 'ALPHA' ],
        noOutputGraph: true,
        noTimeInterval: true,
        includeIntegrations: true,
    },

    AlarmStats: {
        textKey: '6.1241.0.6',
        columnNames: [ 'Farm', 'Date', 'Interval', 'Total', 'FalsePositive', 'RealNonActionable', 'RealActionable', 'Unmarked' ],
        columnSorts: ['ALPHA', 'DATE', 'TIME', 'NUMBER', 'NUMBER', 'NUMBER', 'NUMBER', 'NUMBER'],
        includeIntegrations: true,
    },

    FaceRec: {
        textKey: 'Reports_FaceRecReport_Name',
        columnNames: [ 'Block Number', 'Block Time Range', 'Date', 'Source Time', 'Source Camera', 'Target Time', 'Target Camera', 'Duration', 'Score', 'Source Thumbnail', 'Target Thumbnail' ], // tslint:disable-line:max-line-length
        columnSorts: [ 'NUMBER', 'ALPHA', 'DATE', 'TIME', 'ALPHA', 'TIME', 'ALPHA', 'NUMBER', 'NUMBER', null, null ],
        noOutputGraph: true,
        noTimeInterval: true,
        hasExitDevices: true,
    },

    HeatMap: {
        textKey: '6.1241.0.3',
        columnNames: [ 'Date', 'Time', 'Zone', 'BelowThresholdCount', 'AboveThresholdCount', 'BelowThresholdTime', 'AboveThresholdTime' ],
        columnSorts: [ 'DATE', 'TIME', 'ALPHA', 'NUMBER', 'NUMBER', 'NUMBER', 'NUMBER' ],
        singleCamera: true,
        noOutputGraph: true,
        hasSensitivity: true,
        hasHeatMap: true,
    },

    InStock: {
        textKey: '6.9622.4347',
        columnNames: [ 'Date', 'Interval', 'Shelf.' ],
        columnSorts: [ 'DATE', 'TIME', 'NUMBER' ],
    },

    LPR: {
        textKey: 'Reports_LPRReport_Name',
        columnNames: [ 'Date', 'Time', 'CameraNm', 'LicensePlate', 'Region', 'List', 'Description', 'Thumbnail' ],
        columnSorts: [ 'DATE', 'TIME', 'ALPHA', 'ALPHA', 'ALPHA', 'ALPHA', 'ALPHA', null ],
        noOutputGraph: true,
        noTimeInterval: true,
        hasFilter: true,
    },

    ObjectCounts: {
        textKey: '6.1241.0.4',
        columnNames: [ 'Date', 'Interval', 'CameraNm', 'People', 'Cars', 'Unknown', 'Total' ],
        columnSorts: [ 'DATE', 'TIME', 'ALPHA', 'NUMBER', 'NUMBER', 'NUMBER', 'NUMBER' ],
    },

    ObjectStats: {
        textKey: '6.1241.0.5',
        columnNames: [ 'Date', 'Interval', 'CameraNm', 'Count', 'Dwell', 'Occupancy', 'Activity' ],
        columnSorts: [ 'DATE', 'TIME', 'ALPHA', 'NUMBER', 'NUMBER', 'NUMBER', 'NUMBER' ],
    },

    UserActivity: {
        textKey: 'Reports_UserActivity_Name',
        columnNames: [ 'ID', 'Date', 'Time', 'Description', 'Status', 'User' ],
        columnSorts: [ 'ID', 'DATE', 'TIME', 'ALPHA', 'ALPHA', 'ALPHA' ],
        noOutputGraph: true,
        noTimeInterval: true,
        noDevices: true,
    },

    OverheadPeopleCount: {
        textKey: 'Reports_OverheadPeopleCount_Name',
        columnNames: [ 'Date', 'CameraNm', 'IntervalStart', 'IntervalEnd', 'PeopleIn', 'PeopleOut'],
        columnSorts: [ 'DATE', 'ALPHA', 'TIME', 'TIME', 'NUMBER', 'NUMBER'],
        hasLine: true,
        hasSummary: true,
        requiresAiObjectTracking: true,
    },

    RetentionTime: {
        textKey: 'Reports_RetentionTime_Name',
        columnNames: ['ID', 'CameraNm', 'CameraIp', 'Stream', 'Server', 'RecordingStartTime', 'RecordingEndTime', 'RetentionDuration', 'NormalLength', 'AbnormalLength', 'TotalLength', 'RecordingInterruptions', 'RecordingIntegrityRate'],
        columnSorts: ['ALPHA', 'ALPHA', 'ALPHA', 'ALPHA', 'ALPHA', 'ALPHA', 'ALPHA', 'NUMBER', 'ALPHA', 'ALPHA', 'ALPHA', 'NUMBER', 'ALPHA'],
        noOutputGraph: true,
        noTimeRange: true,
        noTimeInterval: true,
    },

    CameraActivity: {
        textKey: 'Reports_CameraActivity_Name',
        columnNames: ['Date', 'ID', 'CameraNm', 'User', 'Connected', 'Disonnected', 'Duration'],
        columnSorts: ['DATE', 'ALPHA', 'ALPHA', 'ALPHA', 'TIME', 'TIME', 'TIME'],
        noOutputGraph: true,
        hasUsernames: true,
        noTimeStartEnd: true,
    },
};

const validateReportType = (reportType: ReportType) => {
    if (!reportSpecs[reportType]) {
        throw new Error(`Report type "${reportType}" is invalid`);
    }
};

export const hasFeature = (reportType: ReportType, feature: keyof ReportSpecsFeatures) => {
    validateReportType(reportType);
    return !!reportSpecs[reportType][feature];
};

export const getReportNameTextKey = (reportType: ReportType) => {
    validateReportType(reportType);
    return reportSpecs[reportType].textKey;
};

export const getColumnSortType = (reportType: ReportType, columnName: string | null): SortType => {
    if (!columnName) {
        return null;
    }
    validateReportType(reportType);
    const index = reportSpecs[reportType].columnNames.indexOf(columnName);
    if (index < 0) {
        console.error(`Report type "${reportType}" has no column named "${columnName}"`);
        return null;
    }
    return reportSpecs[reportType].columnSorts[index];
};

export const getNumberColumns = (reportType: ReportType) => {
    validateReportType(reportType);
    const { columnNames, columnSorts } = reportSpecs[reportType];
    return columnNames.filter((_, index) => columnSorts[index] === 'NUMBER');
};

export const reportTypeSorter = (t: TranslationFunction) => (a: ReportType, b: ReportType): number => {
    const aName: string = t(getReportNameTextKey(a));
    const bName: string = t(getReportNameTextKey(b));
    return aName.localeCompare(bName);
};



// WEBPACK FOOTER //
// ./src/logic/reports/reportType.ts